// apps/spa/src/components/reminders/AutoDisabledBadge.tsx
// Phase 5 (Plan 05-02) — Auto-disabled badge surfacing the cron loop's
// disabledReason='auto_failure' transition. CONTEXT-locked surface:
// - Amber Badge with text 'Auto-disabled'
// - Tooltip showing last error context + last-failed relative time
// - Click navigates to /reminders/:id/edit (Save IS the recovery action)
//
// Trigger: reminder.disabledReason === 'auto_failure'.
// When disabledReason === 'manual' OR null, this component is NOT rendered;
// the existing Switch toggle handles those states.
//
// The exact last-error string lives in the audit log (per-reminder history
// panel from Phase 04-02). For v1 we surface a generic message at the
// reminder list level — the edit page (linked below) shows the full
// dispatch history for context.

import { Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import type { Reminder } from '@guardian/shared';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface AutoDisabledBadgeProps {
  reminder: Reminder;
}

export function AutoDisabledBadge({ reminder }: AutoDisabledBadgeProps) {
  const lastFailedAt = reminder.failedAt ?? reminder.updatedAt;
  const relative = formatDistanceToNow(new Date(lastFailedAt), { addSuffix: true });
  const errorSummary = 'Auto-disabled after 3 consecutive failures';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Link
            to={`/reminders/${reminder.id}/edit`}
            aria-label={`Auto-disabled. ${errorSummary}. Click to fix and re-enable.`}
            data-testid={`auto-disabled-badge-${reminder.id}`}
            className="inline-flex"
          >
            <Badge
              variant="outline"
              className="border-amber-500/50 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
            >
              Auto-disabled
            </Badge>
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            <div>{errorSummary}</div>
            <div className="text-muted-foreground">Last failed {relative}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
